import { Fragment, useMemo, useRef, useState } from 'react';
import {
  Upload, Loader2, Trash2, ShieldCheck, Undo2, Users, Percent,
  Receipt, Info, CalendarDays, TrendingDown, FileSpreadsheet, MapPin,
  ChevronRight, ChevronDown, Search, Filter, Network,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useGet, usePost } from '@/lib/useData';
import { useFilters } from '@/context/FilterContext';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendAreaChart } from '@/components/charts/TrendAreaChart';
import { HorizontalBars } from '@/components/charts/HorizontalBars';

const C_PRE = '#F59E0B';     // pré-attrition (ambre)
const C_INIT = '#EF4444';    // attrition initiale (rouge)
const C_GUAR = 'var(--tenant-accent-600)';

const fmtInt = (n) => (n ?? 0).toLocaleString('fr-FR');
const fmtPct = (n) => (n === null || n === undefined ? '—' : `${(n).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`);
const round1 = (n) => Math.round((n + Number.EPSILON) * 10) / 10;
const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return ''; }
};

const KpiCard = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <Card className="p-4" data-testid={testid}>
    <div className="mb-2 flex items-center justify-between">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl"
        style={{ background: `color-mix(in srgb, ${accent} 12%, transparent)`, color: accent }}>
        <Icon className="h-[18px] w-[18px]" />
      </span>
    </div>
    <div className="font-display text-2xl font-bold leading-none text-foreground" data-testid={`${testid}-value`}>{value}</div>
    <div className="mt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
    {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
  </Card>
);

// Zone de dépôt multi-fichiers : on dépose autant de fichiers qu'on veut
// (facturation + attrition initiale, tous mois confondus). Le serveur détecte
// pour chacun le TYPE et la PÉRIODE (mois de création des bulletins).
const MultiDropZone = ({ onFiles, busy, progress, testid }) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const pick = (files) => {
    const list = Array.from(files || []).filter((f) => /\.(xlsx|xls|zip)$/i.test(f.name));
    if (!list.length) { toast.error('Déposez des fichiers Excel (.xlsx) ou une archive .zip'); return; }
    onFiles(list);
  };
  return (
    <div
      role="button" tabIndex={0}
      onClick={() => !busy && inputRef.current?.click()}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !busy) inputRef.current?.click(); }}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); if (!busy) pick(e.dataTransfer.files); }}
      className={`flex cursor-pointer flex-col items-center justify-center gap-2.5 rounded-2xl border-2 border-dashed p-6 text-center transition-colors duration-200 ${busy ? 'pointer-events-none opacity-80' : 'hover:accent-border hover:accent-bg-soft'}`}
      style={{ borderColor: drag ? 'var(--tenant-accent-500)' : undefined }}
      data-testid={testid}
    >
      <input ref={inputRef} type="file" accept=".xlsx,.xls,.zip" multiple className="hidden"
        onChange={(e) => { pick(e.target.files); e.target.value = ''; }} data-testid={`${testid}-input`} />
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl"
        style={{ background: 'var(--tenant-accent-050)', color: 'var(--tenant-accent-600)' }}>
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
      </span>
      <div className="font-display text-base font-semibold">
        {busy && progress ? `Analyse… ${progress.done}/${progress.total}` : 'Déposez vos fichiers Amnesty'}
      </div>
      <p className="max-w-md text-xs text-muted-foreground">
        Glissez autant de fichiers que nécessaire — ou une <strong>archive .zip</strong> contenant tout
        l'historique. <strong>Export de facturation</strong> (pré-attrition) et <strong>attrition initiale</strong>,
        tous mois confondus : le type et la période (mois de création des bulletins) sont{' '}
        <strong>détectés automatiquement</strong> et rangés à la bonne cohorte.
      </p>
    </div>
  );
};

const SectionTitle = ({ icon: Icon, title, desc, right }) => (
  <div className="mb-3 flex items-start justify-between gap-2">
    <div>
      <h3 className="flex items-center gap-2 font-display text-base font-semibold">
        {Icon && <Icon className="h-4 w-4 accent-text" />} {title}
      </h3>
      {desc && <p className="mt-0.5 text-xs text-muted-foreground">{desc}</p>}
    </div>
    {right}
  </div>
);

// Ligne de campagne lisible : libellé (canal + région) + code en sous-ligne.
const CampaignRow = ({ cp, tone }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/50 px-2.5 py-1.5" data-testid={`rb-camp-${cp.code}`}>
    <div className="min-w-0">
      <div className="truncate text-xs font-semibold">{cp.label || cp.canal || cp.code}</div>
      <div className="truncate text-[10px] text-muted-foreground">
        {[cp.canal, cp.region].filter(Boolean).join(' · ') || 'Canal/région inconnu'}
        {' · '}<span className="font-mono">{cp.code}</span>
      </div>
    </div>
    <span className="shrink-0 font-display text-sm font-bold tabular-nums" style={{ color: tone }}>{fmtInt(cp.count)}</span>
  </div>
);

// Petite puce « source » (fichier + date d'import) pour la traçabilité.
const SourceChip = ({ label, source, tone }) => {
  if (!source) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] text-muted-foreground"
          style={{ borderColor: `color-mix(in srgb, ${tone} 35%, transparent)` }} data-testid="rb-source-chip">
          <FileSpreadsheet className="h-3 w-3" style={{ color: tone }} /> {label}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="text-xs font-medium">{source.filename || 'fichier'}</div>
        <div className="text-[11px] text-muted-foreground">Importé le {fmtDate(source.uploaded_at)}{source.uploaded_by ? ` · ${source.uploaded_by}` : ''}</div>
      </TooltipContent>
    </Tooltip>
  );
};

const MetricCell = ({ n, pct, color, strong }) => (
  <TableCell className="text-right">
    <div className={`tabular-nums ${strong ? 'font-bold' : 'font-medium'}`} style={color ? { color } : undefined}>
      {n === null || n === undefined ? '—' : fmtInt(n)}
    </div>
    {pct !== null && pct !== undefined && (
      <div className="text-[10px] tabular-nums text-muted-foreground">{fmtPct(pct)}</div>
    )}
  </TableCell>
);

export const RemboursementsSection = ({ association = 'AMNESTY' }) => {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [importRecap, setImportRecap] = useState(null);
  const q = useGet('/attrition/remboursements', { association }, true);
  const data = q.data || {};
  const summary = data.summary || {};
  const cohorts = data.cohorts || [];
  const byYear = data.by_year || [];
  const byRecruteur = data.by_recruteur || [];

  // --- Entonnoir par recruteur : suit les filtres globaux (période + dimensions). ---
  const filters = useFilters();
  const recrBody = useMemo(() => ({
    association,
    start: filters?.period?.start || null,
    end: filters?.period?.end || null,
    campaign_codes: filters?.dims?.campaign_codes || [],
    regions: filters?.dims?.regions || [],
    lieux: filters?.dims?.lieux || [],
    responsables: filters?.dims?.responsables || [],
    canaux: filters?.dims?.canaux || [],
  }), [association, filters?.period, filters?.dims]);
  const recrQ = usePost('/attrition/remboursements/recruteurs', recrBody, true);
  const recrAll = recrQ.data?.recruteurs || [];
  const [recrSearch, setRecrSearch] = useState('');
  // Bascule de la référence des taux du tableau recruteur : % Brut / % Net livré / % Base Amnesty.
  const [recrRateRef, setRecrRateRef] = useState('net');
  // Dépliage mois par mois de chaque recruteur (comme l'entonnoir annuel).
  const [expandedRecrs, setExpandedRecrs] = useState(() => new Set());
  const toggleRecr = (code) => setExpandedRecrs((prev) => {
    const next = new Set(prev);
    next.has(code) ? next.delete(code) : next.add(code);
    return next;
  });
  const recrFunnel = useMemo(() => {
    const term = recrSearch.trim().toLowerCase();
    if (!term) return recrAll;
    return recrAll.filter((r) => (r.nom || '').toLowerCase().includes(term)
      || (r.code || '').toLowerCase().includes(term));
  }, [recrAll, recrSearch]);
  const recrTotals = useMemo(() => recrFunnel.reduce((t, r) => ({
    bruts: t.bruts + (r.bruts || 0), net_livre: t.net_livre + (r.net_livre || 0),
    pre: t.pre + (r.pre || 0), init: t.init + (r.init || 0), crees: t.crees + (r.crees || 0),
  }), { bruts: 0, net_livre: 0, pre: 0, init: 0, crees: 0 }), [recrFunnel]);
  const filtersActive = (filters?.activeCount || 0) > 0;

  const [expandedYears, setExpandedYears] = useState(() => new Set());
  const toggleYear = (y) => setExpandedYears((prev) => {
    const next = new Set(prev);
    next.has(y) ? next.delete(y) : next.add(y);
    return next;
  });
  // Regroupe les cohortes par année (mois triés) pour le détail dépliable.
  const cohortsByYear = useMemo(() => {
    const map = {};
    cohorts.forEach((c) => { (map[c.cohort_year || 0] ||= []).push(c); });
    Object.values(map).forEach((arr) => arr.sort((a, b) => (a.cohort_month || 0) - (b.cohort_month || 0)));
    return map;
  }, [cohorts]);
  const monthLabel = (c) => (c.cohort_label || '').replace(/\s*\d{4}$/, '') || c.cohort_label;
  const cohortTauxGar = (c) => {
    const base = c.pre?.mandats_crees_total || 0;
    return base ? round1((c.combined_total || 0) / base * 100) : null;
  };

  // Entonnoir : bascule de la référence des taux (brut terrain vs net livré).
  const [rateRef, setRateRef] = useState('net');
  const amnesty = rateRef === 'amnesty';
  const makeFunnel = (bruts, livres, pre, init, crees, mode = rateRef) => {
    const amn = mode === 'amnesty';
    const L = livres || 0; const P = pre || 0; const I = init || 0;
    const net = Math.max(L - P - I, 0);
    const hasBruts = bruts !== null && bruts !== undefined && bruts > 0;
    const annul = hasBruts ? Math.max(bruts - L, 0) : null;
    const C = crees || 0;
    const facturesAmn = Math.max(C - P, 0);          // mandats facturés = créés − pré-attrition
    const netAmnesty = Math.max(facturesAmn - I, 0); // net final Amnesty = facturés − attrition initiale
    // Dénominateurs des taux selon le mode choisi :
    //  - brut    : tout rapporté au brut terrain (Evergiving)
    //  - net     : tout rapporté au net livré (bulletins finalisés Evergiving)
    //  - amnesty : pré ÷ mandats créés, attrition initiale ÷ mandats facturés (méthode Amnesty)
    const baseAll = mode === 'brut' ? (hasBruts ? bruts : 0) : L;
    const preRef = amn ? C : baseAll;
    const initRef = amn ? facturesAmn : baseAll;
    const perteRef = amn ? C : baseAll;
    const perte = (mode === 'brut' && hasBruts) ? bruts - net : L - net;
    const pctOn = (n, r) => (r ? round1((n / r) * 100) : null);
    return {
      bruts: hasBruts ? bruts : null, livres: L, net, annul,
      tauxWC: hasBruts ? round1((annul / bruts) * 100) : null,
      pre: P, tauxPre: pctOn(P, preRef),
      init: I, tauxInit: pctOn(I, initRef),
      perte, tauxPerte: pctOn(perte, perteRef),
      crees: C, factures: facturesAmn, netAmnesty,
    };
  };
  // Rend les cellules de l'entonnoir selon le mode (Evergiving vs lecture native Amnesty).
  const FunnelCells = ({ f, strong, mode = rateRef }) => ((mode === 'amnesty') ? (
    <>
      <MetricCell n={f.crees || null} strong={strong} />
      <MetricCell n={f.pre} pct={f.tauxPre} color={C_PRE} strong={strong} />
      <MetricCell n={f.crees ? f.factures : null} strong={strong} />
      <MetricCell n={f.init} pct={f.tauxInit} color={C_INIT} strong={strong} />
      <MetricCell n={f.crees ? f.netAmnesty : null} strong color="var(--tenant-accent-600)" />
      <MetricCell n={f.perte} pct={f.tauxPerte} color="#E11D48" strong={strong} />
    </>
  ) : (
    <>
      <MetricCell n={f.bruts} strong={strong} />
      <MetricCell n={f.annul} pct={f.tauxWC} color="#64748B" strong={strong} />
      <MetricCell n={f.livres} strong={strong} />
      <MetricCell n={f.pre} pct={f.tauxPre} color={C_PRE} strong={strong} />
      <MetricCell n={f.init} pct={f.tauxInit} color={C_INIT} strong={strong} />
      <MetricCell n={f.net} strong color="var(--tenant-accent-600)" />
      <MetricCell n={f.perte} pct={f.tauxPerte} color="#E11D48" strong={strong} />
    </>
  ));
  // En-têtes de colonnes de l'entonnoir, selon le mode choisi (réutilisé annuel + recruteur).
  const FunnelHeadCells = ({ mode }) => ((mode === 'amnesty') ? (
    <>
      <TableHead className="text-right">Mandats créés</TableHead>
      <TableHead className="text-right">Pré-attr.</TableHead>
      <TableHead className="text-right">Mandats facturés</TableHead>
      <TableHead className="text-right">Attr. init.</TableHead>
      <TableHead className="text-right">Net final</TableHead>
      <TableHead className="text-right">Perte totale</TableHead>
    </>
  ) : (
    <>
      <TableHead className="text-right">Bruts</TableHead>
      <TableHead className="text-right">Annulé WC</TableHead>
      <TableHead className="text-right">Net livré</TableHead>
      <TableHead className="text-right">Pré-attr.</TableHead>
      <TableHead className="text-right">Attr. init.</TableHead>
      <TableHead className="text-right">Net final</TableHead>
      <TableHead className="text-right">Perte totale</TableHead>
    </>
  ));

  const uploadMany = async (files) => {
    const list = Array.from(files);
    setBusy(true);
    setProgress({ done: 0, total: list.length });
    let okFiles = 0;      // fichiers Excel importés (unitaires + contenus de zip)
    let failFiles = 0;
    const allWarnings = [];
    const recap = [];     // reçu d'import : où chaque fichier a été rattaché
    for (let i = 0; i < list.length; i += 1) {
      try {
        const fd = new FormData();
        fd.append('file', list[i]);
        fd.append('association', association);
        const { data: res } = await api.post('/attrition/remboursements', fd);
        if (res.batch) {
          okFiles += res.imported || 0;
          failFiles += res.failed || 0;
          (res.results || []).forEach((r) => {
            (r.warnings || []).forEach((w) => allWarnings.push(w));
            recap.push({ filename: r.filename, cohort_label: r.cohort_label, filled: r.filled || [], ok: true });
          });
          (res.errors || []).forEach((er) => {
            toast.error(`${er.filename} : ${er.error}`);
            recap.push({ filename: er.filename, error: er.error, ok: false });
          });
        } else {
          okFiles += 1;
          (res.warnings || []).forEach((w) => allWarnings.push(w));
          recap.push({ filename: list[i].name, cohort_label: res.cohort_label, filled: res.filled || [], ok: true });
        }
      } catch (e) {
        failFiles += 1;
        const msg = e?.response?.data?.detail || 'import impossible';
        toast.error(`${list[i].name} : ${msg}`);
        recap.push({ filename: list[i].name, error: msg, ok: false });
      }
      setProgress({ done: i + 1, total: list.length });
    }
    setBusy(false);
    setProgress(null);
    setImportRecap(recap.length ? recap : null);
    if (okFiles) toast.success(`${okFiles} fichier${okFiles > 1 ? 's' : ''} importé${okFiles > 1 ? 's' : ''} — périodes rattachées automatiquement.`);
    if (failFiles && !okFiles) toast.error(`${failFiles} fichier(s) non importé(s).`);
    [...new Set(allWarnings)].slice(0, 3).forEach((w) => toast.warning(w));
    await q.refetch();
  };

  const removeCohort = async (id) => {
    try {
      await api.delete(`/attrition/remboursements/${id}`);
      toast.success('Cohorte supprimée');
      await q.refetch();
    } catch { toast.error('Suppression impossible'); }
  };

  // Séries d'évolution par cohorte (ordre chronologique renvoyé par l'API).
  const evoData = cohorts.map((c) => ({
    period: c.cohort_label,
    pre: c.pre ? c.pre.total : 0,
    init: c.init ? c.init.total : 0,
  }));
  const recrData = byRecruteur.slice(0, 15).map((r) => ({
    key: r.nom || r.code, value: r.pre_total, code: r.code,
  }));

  if (q.isLoading) {
    return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="rb-loading">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>;
  }

  return (
    <div className="space-y-4" data-testid="attr-remboursements">
      {/* Bandeau explicatif */}
      <div className="flex items-start gap-2 rounded-xl border border-blue-300/50 bg-blue-50/60 p-3 text-xs text-blue-900 dark:border-blue-900/40 dark:bg-blue-950/20 dark:text-blue-200" data-testid="rb-intro">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Suivi des bulletins <strong>remboursés</strong> à Amnesty au titre de la garantie (0/1 prélèvement) :
          la <strong>pré-attrition</strong> (0 prélèvement, déduite dès la facturation) et
          l'<strong>attrition initiale</strong> (bulletins arrêtés à &lt; 2 prélèvements, reçue ~3 mois après).
          Chaque remboursement est rattaché au <strong>mois de création du bulletin</strong> (pas au mois de réception du fichier) —
          les chiffres sont lus directement des feuilles de synthèse Amnesty, sans estimation.
        </span>
      </div>

      {/* Zone d'import multi-fichiers */}
      <MultiDropZone onFiles={uploadMany} busy={busy} progress={progress} testid="rb-upload" />

      {/* Reçu d'import : où chaque fichier a été rattaché (cohorte = mois de création) */}
      {importRecap && importRecap.length > 0 && (
        <Card className="p-4" data-testid="rb-import-recap">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="flex items-center gap-2 text-sm font-semibold">
              <ShieldCheck className="h-4 w-4 accent-text" /> Reçu du dernier import
            </h4>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs"
              onClick={() => setImportRecap(null)} data-testid="rb-import-recap-close">
              Fermer
            </Button>
          </div>
          <ul className="space-y-1.5">
            {importRecap.map((r, i) => (
              <li key={`${r.filename}-${i}`} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs"
                data-testid="rb-import-recap-item">
                {r.ok ? (
                  <ShieldCheck className="h-3.5 w-3.5 shrink-0" style={{ color: C_GUAR }} />
                ) : (
                  <Info className="h-3.5 w-3.5 shrink-0 text-destructive" />
                )}
                <span className="font-mono text-muted-foreground">{r.filename}</span>
                {r.ok ? (
                  <>
                    <span className="text-muted-foreground">→ rattaché à</span>
                    <Badge variant="secondary" className="font-semibold capitalize">{r.cohort_label}</Badge>
                    {(r.filled || []).map((f) => (
                      <Badge key={f} variant="outline" className="text-[10px]">{f}</Badge>
                    ))}
                  </>
                ) : (
                  <span className="text-destructive">{r.error}</span>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Chaque fichier met à jour <strong>uniquement sa cohorte</strong> (mois de création des bulletins) — les autres mois
            et l'historique ne sont jamais écrasés. Vérifiez que la période de rattachement ci-dessus correspond bien au mois
            de création attendu.
          </p>
        </Card>
      )}

      {cohorts.length === 0 ? (
        <Card className="p-8 text-center" data-testid="rb-empty">
          <h3 className="font-display text-lg font-bold">Aucun remboursement importé</h3>
          <p className="mt-1 text-sm text-muted-foreground">Déposez vos fichiers Amnesty ci-dessus (facturation et/ou attrition initiale) pour démarrer le suivi.</p>
        </Card>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard icon={ShieldCheck} label="Remboursés (garantie)" accent={C_GUAR} testid="rb-kpi-guarantee"
              value={fmtInt(summary.guarantee_total)}
              sub={`taux ${fmtPct(summary.taux_guarantee)} de remboursement · ${summary.n_cohorts || 0} cohorte${(summary.n_cohorts || 0) > 1 ? 's' : ''}`} />
            <KpiCard icon={Undo2} label="Pré-attrition" accent={C_PRE} testid="rb-kpi-pre"
              value={fmtInt(summary.pre_total)} sub={`taux ${fmtPct(summary.taux_pre)} · / mandats créés`} />
            <KpiCard icon={TrendingDown} label="Attrition initiale" accent={C_INIT} testid="rb-kpi-init"
              value={fmtInt(summary.init_total)} sub={`taux ${fmtPct(summary.taux_init)} · / mandats facturés`} />
            <KpiCard icon={Receipt} label="Mandats créés (base)" accent="#64748B" testid="rb-kpi-base"
              value={fmtInt(summary.mandats_crees_total)} sub={`${fmtInt(summary.mandats_factures_total)} facturés`} />
          </div>

          {/* Évolution par cohorte */}
          <Card className="p-5" data-testid="rb-evolution-card">
            <SectionTitle icon={CalendarDays} title="Évolution par cohorte"
              desc="Bulletins remboursés par mois de création (pré-attrition vs attrition initiale)" />
            <TrendAreaChart
              data={evoData}
              xKey="period"
              series={[
                { key: 'pre', name: 'Pré-attrition', color: C_PRE },
                { key: 'init', name: 'Attrition initiale', color: C_INIT },
              ]}
              formatTip={(v) => fmtInt(v)}
              formatY={(v) => fmtInt(v)}
            />
          </Card>

          {/* Entonnoir par année — dépliable mois par mois */}
          <Card className="p-5" data-testid="rb-year-card">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="flex items-center gap-2 font-display text-base font-semibold">
                  <Percent className="h-4 w-4 accent-text" /> Entonnoir par année
                </h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Du brut terrain au net final — cliquez une année pour le détail mois par mois.
                </p>
              </div>
              <div className="inline-flex rounded-lg border p-0.5" data-testid="rb-rate-toggle">
                <button type="button" onClick={() => setRateRef('brut')} data-testid="rb-rate-brut"
                  className="rounded-md px-2.5 py-1 text-xs font-medium transition-colors"
                  style={rateRef === 'brut' ? { background: 'var(--tenant-accent-600)', color: '#fff' } : undefined}>
                  % / Brut
                </button>
                <button type="button" onClick={() => setRateRef('net')} data-testid="rb-rate-net"
                  className="rounded-md px-2.5 py-1 text-xs font-medium transition-colors"
                  style={rateRef === 'net' ? { background: 'var(--tenant-accent-600)', color: '#fff' } : undefined}>
                  % / Net livré
                </button>
                <button type="button" onClick={() => setRateRef('amnesty')} data-testid="rb-rate-amnesty"
                  className="rounded-md px-2.5 py-1 text-xs font-medium transition-colors"
                  style={rateRef === 'amnesty' ? { background: 'var(--tenant-accent-600)', color: '#fff' } : undefined}>
                  % Base Amnesty
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[130px]">Année</TableHead>
                    <FunnelHeadCells mode={rateRef} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byYear.map((y) => {
                    const open = expandedYears.has(y.year);
                    const months = cohortsByYear[y.year] || [];
                    const f = makeFunnel(y.bruts_total, y.net_livre_total, y.pre_total, y.init_total, y.crees_total);
                    return (
                      <Fragment key={y.year}>
                        <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => toggleYear(y.year)} data-testid={`rb-year-${y.year}`}>
                          <TableCell className="font-semibold">
                            <button type="button" className="inline-flex items-center gap-1.5" aria-expanded={open}
                              data-testid={`rb-year-toggle-${y.year}`}
                              onClick={(e) => { e.stopPropagation(); toggleYear(y.year); }}>
                              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              {y.year}
                            </button>
                          </TableCell>
                          <FunnelCells f={f} strong />
                        </TableRow>
                        {open && months.map((c) => {
                          const cf = makeFunnel(c.bruts, c.net_livre, c.pre?.total, c.init?.total, c.pre?.mandats_crees_total);
                          return (
                            <TableRow key={c.id} className="bg-muted/30 text-xs" data-testid={`rb-month-${c.id}`}>
                              <TableCell className="pl-9 capitalize text-muted-foreground">{monthLabel(c)}</TableCell>
                              <FunnelCells f={cf} />
                            </TableRow>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                  {/* Total général */}
                  {(() => {
                    const tf = makeFunnel(summary.bruts_total, summary.net_livre_total, summary.pre_total, summary.init_total, summary.mandats_crees_total);
                    return (
                      <TableRow className="border-t-2 bg-muted/40 font-semibold" data-testid="rb-year-total">
                        <TableCell className="font-bold">Total</TableCell>
                        <FunnelCells f={tf} strong />
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              {!amnesty && (
                <>Net livré = bulletins finalisés (données Evergiving, source active dédoublonnée) = brut − annulés au welcome call.
                Net final = net livré − pré-attrition − attrition initiale.{' '}</>
              )}
              {rateRef === 'brut' && 'Taux calculés par rapport au brut terrain.'}
              {rateRef === 'net' && 'Taux calculés par rapport au net livré (bulletins finalisés Evergiving).'}
              {amnesty && 'Lecture Amnesty : mandats facturés = mandats créés − pré-attrition. Net final = mandats facturés − attrition initiale. Taux : pré-attrition ÷ mandats créés, attrition initiale ÷ mandats facturés.'}
            </p>
          </Card>

          {/* Par recruteur (pré-attrition) — graphique */}
          <Card className="p-5" data-testid="rb-recruteur-card">
              <SectionTitle icon={Users} title="Pré-attrition par recruteur"
                desc="Bulletins 0 prélèvement par recruteur (cumul toutes cohortes)" />
              {recrData.length ? (
                <HorizontalBars data={recrData} dataKey="value" labelKey="key" color={C_PRE}
                  height={Math.max(220, recrData.length * 28)} yWidth={150}
                  formatTip={(v) => `${fmtInt(v)} bulletins`} />
              ) : (
                <p className="py-8 text-center text-sm text-muted-foreground">Aucune donnée recruteur disponible.</p>
              )}
            </Card>

          {/* Entonnoir par recruteur — suit les filtres actifs + recherche */}
          <Card className="p-5" data-testid="rb-recruteur-funnel-card">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="flex items-center gap-2 font-display text-base font-semibold">
                  <Network className="h-4 w-4 accent-text" /> Entonnoir par recruteur
                </h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Mêmes colonnes que l'entonnoir annuel, déclinées par recruteur — {filtersActive
                    ? 'selon les filtres actifs (période, campagne, région…)'
                    : 'sur toute la période (ajustez les filtres en haut de page)'}. Cliquez un
                  recruteur pour le détail mois par mois.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex rounded-lg border p-0.5" data-testid="rb-recr-rate-toggle">
                  <button type="button" onClick={() => setRecrRateRef('brut')} data-testid="rb-recr-rate-brut"
                    className="rounded-md px-2.5 py-1 text-xs font-medium transition-colors"
                    style={recrRateRef === 'brut' ? { background: 'var(--tenant-accent-600)', color: '#fff' } : undefined}>
                    % / Brut
                  </button>
                  <button type="button" onClick={() => setRecrRateRef('net')} data-testid="rb-recr-rate-net"
                    className="rounded-md px-2.5 py-1 text-xs font-medium transition-colors"
                    style={recrRateRef === 'net' ? { background: 'var(--tenant-accent-600)', color: '#fff' } : undefined}>
                    % / Net livré
                  </button>
                  <button type="button" onClick={() => setRecrRateRef('amnesty')} data-testid="rb-recr-rate-amnesty"
                    className="rounded-md px-2.5 py-1 text-xs font-medium transition-colors"
                    style={recrRateRef === 'amnesty' ? { background: 'var(--tenant-accent-600)', color: '#fff' } : undefined}>
                    % Base Amnesty
                  </button>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={recrSearch}
                    onChange={(e) => setRecrSearch(e.target.value)}
                    placeholder="Rechercher un recruteur (nom ou code)…"
                    className="h-9 pl-8"
                    data-testid="rb-recruteur-search"
                  />
                </div>
              </div>
            </div>

            {filtersActive && (
              <div className="mb-3 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Filter className="h-3 w-3" />
                Filtres appliqués — {fmtInt(recrAll.length)} recruteur{recrAll.length > 1 ? 's' : ''} sur la sélection.
              </div>
            )}

            {recrQ.isLoading ? (
              <Skeleton className="h-64 w-full rounded-xl" data-testid="rb-recruteur-funnel-loading" />
            ) : recrAll.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground" data-testid="rb-recruteur-funnel-empty">
                Aucun recruteur sur cette sélection.
              </p>
            ) : (
              <>
                <div className="max-h-[520px] overflow-y-auto rounded-xl border">
                  <Table>
                    <TableHeader className="sticky top-0 z-10 bg-card">
                      <TableRow>
                        <TableHead className="min-w-[220px]">Recruteur</TableHead>
                        <FunnelHeadCells mode={recrRateRef} />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recrFunnel.map((r) => {
                        const open = expandedRecrs.has(r.code);
                        const months = r.months || [];
                        const rf = makeFunnel(r.bruts, r.net_livre, r.pre, r.init, r.crees, recrRateRef);
                        return (
                          <Fragment key={r.code}>
                            <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => toggleRecr(r.code)}
                              data-testid={`rb-recruteur-funnel-${r.code}`}>
                              <TableCell>
                                <button type="button" className="flex items-start gap-1.5 text-left" aria-expanded={open}
                                  data-testid={`rb-recruteur-toggle-${r.code}`}
                                  onClick={(e) => { e.stopPropagation(); toggleRecr(r.code); }}>
                                  <span className="mt-0.5 shrink-0">
                                    {months.length > 0
                                      ? (open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)
                                      : <span className="inline-block h-4 w-4" />}
                                  </span>
                                  <span className="min-w-0">
                                    <span className="block font-medium leading-tight">{r.nom || 'Nom inconnu'}</span>
                                    <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                                      <span className="font-mono">{r.code === '__nonattribue__' ? '—' : r.code}</span>
                                      {r.canal ? <span>· {r.canal}</span> : null}
                                      {r.region ? (
                                        <span className="inline-flex items-center gap-0.5">
                                          · <MapPin className="h-2.5 w-2.5" />{r.region}
                                        </span>
                                      ) : null}
                                    </span>
                                  </span>
                                </button>
                              </TableCell>
                              <FunnelCells f={rf} strong mode={recrRateRef} />
                            </TableRow>
                            {open && months.map((m) => {
                              const mf = makeFunnel(m.bruts, m.net_livre, m.pre, m.init, m.crees, recrRateRef);
                              return (
                                <TableRow key={`${r.code}-${m.year}-${m.month}`} className="bg-muted/30 text-xs"
                                  data-testid={`rb-recruteur-month-${r.code}-${m.year}-${m.month}`}>
                                  <TableCell className="pl-9 capitalize text-muted-foreground">{m.label}</TableCell>
                                  <FunnelCells f={mf} mode={recrRateRef} />
                                </TableRow>
                              );
                            })}
                          </Fragment>
                        );
                      })}
                      {recrFunnel.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={recrRateRef === 'amnesty' ? 7 : 8} className="py-8 text-center text-sm text-muted-foreground">
                            Aucun recruteur ne correspond à « {recrSearch} ».
                          </TableCell>
                        </TableRow>
                      )}
                      {recrFunnel.length > 0 && (() => {
                        const tf = makeFunnel(recrTotals.bruts, recrTotals.net_livre, recrTotals.pre, recrTotals.init, recrTotals.crees, recrRateRef);
                        return (
                          <TableRow className="border-t-2 bg-muted/40 font-semibold" data-testid="rb-recruteur-funnel-total">
                            <TableCell className="font-bold">
                              Total {recrSearch ? '(filtré)' : ''} · {fmtInt(recrFunnel.filter((r) => r.code !== '__nonattribue__').length)} recruteur{recrFunnel.length > 1 ? 's' : ''}
                            </TableCell>
                            <FunnelCells f={tf} strong mode={recrRateRef} />
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Bruts et Net livré proviennent des données de recrutement (Evergiving) ; Pré-attrition, Mandats créés et
                  <strong> Attrition initiale</strong> des fichiers Amnesty, par code recruteur. L'attrition initiale est
                  rattachée au recruteur via le <strong>RUM</strong> du mandat (8 derniers chiffres = identifiant Evergiving) ;
                  le reliquat non rattachable apparaît en ligne <strong>« Non attribué »</strong>.
                  {recrRateRef === 'brut' && ' Taux calculés par rapport au brut terrain.'}
                  {recrRateRef === 'net' && ' Taux calculés par rapport au net livré (bulletins finalisés Evergiving).'}
                  {recrRateRef === 'amnesty' && ' Lecture Amnesty : mandats facturés = créés − pré-attrition ; taux pré-attrition ÷ mandats créés, attrition initiale ÷ mandats facturés.'}
                  {' '}<strong>Mandats créés</strong> et <strong>attrition initiale</strong> par recruteur se remplissent au <strong>ré-import</strong> des fichiers Amnesty : en mode « Base Amnesty », les taux ne sont exacts que sur les cohortes ré-importées (sinon le dénominateur est incomplet).
                </p>
              </>
            )}
          </Card>

          {/* Détail par cohorte */}
          <Card className="p-5" data-testid="rb-cohorts-card">
            <SectionTitle icon={CalendarDays} title="Détail par cohorte"
              desc="Chaque mois de création, avec ses remboursements par campagne (canal & région)." />
            <div className="space-y-3">
              {[...cohorts].reverse().map((c) => (
                <div key={c.id} className="rounded-xl border p-3.5" data-testid={`rb-cohort-${c.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-display text-sm font-bold capitalize">{c.cohort_label}</span>
                      {c.pre && <Badge variant="outline" className="gap-1" style={{ borderColor: C_PRE, color: C_PRE }}>
                        Pré-attr. {fmtInt(c.pre.total)} · {fmtPct(c.pre.taux)}</Badge>}
                      {c.init && <Badge variant="outline" className="gap-1" style={{ borderColor: C_INIT, color: C_INIT }}>
                        Attr. init. {fmtInt(c.init.total)} · {fmtPct(c.init.taux)}</Badge>}
                      {!c.init && <Badge variant="outline" className="text-muted-foreground">Attrition initiale en attente</Badge>}
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 w-8 rounded-lg p-0 text-rose-500 hover:text-rose-600" data-testid={`rb-delete-${c.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Supprimer la cohorte {c.cohort_label} ?</AlertDialogTitle>
                          <AlertDialogDescription>Les données de remboursement de ce mois seront supprimées. Vous pourrez réimporter le fichier.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeCohort(c.id)} className="bg-rose-600 hover:bg-rose-700" data-testid={`rb-delete-confirm-${c.id}`}>Supprimer</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>

                  {/* Traçabilité des fichiers sources */}
                  {(c.pre_source || c.init_source) && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <SourceChip label="Source pré-attrition" source={c.pre_source} tone={C_PRE} />
                      <SourceChip label="Source attrition initiale" source={c.init_source} tone={C_INIT} />
                    </div>
                  )}

                  {/* Par campagne */}
                  <div className="mt-3 grid gap-4 sm:grid-cols-2">
                    {c.pre && (c.pre.campaigns || []).length > 0 && (
                      <div>
                        <div className="mb-1.5 text-xs font-semibold" style={{ color: C_PRE }}>Pré-attrition par campagne</div>
                        <div className="space-y-1.5">
                          {c.pre.campaigns.map((cp) => <CampaignRow key={`pre-${cp.code}`} cp={cp} tone={C_PRE} />)}
                        </div>
                      </div>
                    )}
                    {c.init && (c.init.campaigns || []).length > 0 && (
                      <div>
                        <div className="mb-1.5 text-xs font-semibold" style={{ color: C_INIT }}>Attrition initiale par campagne</div>
                        <div className="space-y-1.5">
                          {c.init.campaigns.map((cp) => <CampaignRow key={`init-${cp.code}`} cp={cp} tone={C_INIT} />)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default RemboursementsSection;
